// File: src/components/OmniCine/Timeline/Clips/ClipChrome.tsx

import React from "react";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { useRangeGlowStyles } from "./useRangeGlow";

type ClipChromeProps = {
  clipId: string;
  className?: string;
  children?: React.ReactNode;
};

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export default function ClipChrome({
  clipId,
  className = "",
  children,
}: ClipChromeProps): JSX.Element {
  const { state } = useOmniCineTimeline();
  const themeVars = useRangeGlowStyles();

  const isSelected = state.selectedClipId === clipId;

  return (
    <div
      className={cx(
        // ✅ Outer: fill parent, allow overlays to escape
        "relative h-full w-full overflow-visible",
        "group",
        className,
      )}
      style={themeVars}
      data-oc-clip-selected={isSelected ? "1" : "0"}
    >
      {/* -------------------------------------------------- */}
      {/* Overlay layer (NOT clipped)                         */}
      {/* Put PerceptStripe/PerceptBadge *before* content.    */}
      {/* -------------------------------------------------- */}
      <div
        className="pointer-events-none absolute inset-0 z-[30]"
        data-oc-clip-chrome-overlay="1"
      >
        {children}
      </div>

      {/* -------------------------------------------------- */}
      {/* Clipped visual body (rounded + overflow-hidden)     */}
      {/* -------------------------------------------------- */}
      <div
        className={cx(
          "relative h-full w-full z-[10]",
          "rounded-lg overflow-hidden bg-white/3 border border-white/10",
          // Selection: inset neon border + inner glow (survives overflow-hidden parents)
          isSelected &&
            "border-cyan-300/40 shadow-[inset_0_0_0_2px_rgba(34,211,238,0.55),inset_0_0_18px_rgba(34,211,238,0.18),0_18px_40px_rgba(0,0,0,0.35)]",
        )}
        data-oc-clip-chrome-body="1"
      >
        {/* NOTE:
           We render children AGAIN inside the body? No — we don't want that.
           So ClipBlock must pass ONLY the clip content here, not overlay.
           To keep this patch minimal without changing ClipBlock signature,
           we split usage: ClipBlock should wrap its content with a body container.

           If you want zero changes to ClipBlock, use the "no-API-change" version below. */}
      </div>
    </div>
  );
}
